export default function Introduction() {
  return (
    <section id="introduction">
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Introduction</h2>

      <div className="space-y-4 text-slate-600 dark:text-slate-400 leading-relaxed">
        <p>
          Bedrock Toolchest is a desktop application for editing Minecraft Bedrock Edition world files.
          It lets you modify level settings, player data, world content, and more — all through a
          straightforward interface. No command-line knowledge required.
        </p>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Key features</h3>
          <ul className="space-y-2 text-sm">
            <li className="flex gap-2"><span className="text-primary-500">•</span>Edit level settings like game mode, difficulty, spawn point, and game rules</li>
            <li className="flex gap-2"><span className="text-primary-500">•</span>Modify player inventories, health, effects, position, and abilities</li>
            <li className="flex gap-2"><span className="text-primary-500">•</span>Manage world entities, portals, maps, and the Ender Dragon fight</li>
            <li className="flex gap-2"><span className="text-primary-500">•</span>Install and remove behavior packs and resource packs</li>
            <li className="flex gap-2"><span className="text-primary-500">•</span>Prune chunks to reduce world file size</li>
            <li className="flex gap-2"><span className="text-primary-500">•</span>Automatic backups before every save</li>
          </ul>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Getting started</h3>
          <ol className="space-y-2 text-sm list-decimal list-inside">
            <li>Download the latest version from the <a href="/download" className="text-primary-500 hover:text-primary-600 underline">download page</a>.</li>
            <li>Run the .exe file — no installation or setup needed.</li>
            <li>Click <strong>Open World</strong> and select a Minecraft Bedrock world folder from your computer, or pick one from the All Worlds list. You can sort the list by world name, folder name, or last modified to find worlds faster. You can also click <strong>Manage Addons</strong> to browse, import, and remove globally installed addons without loading a world.</li>
            <li>Use the tabs to browse and edit your world.</li>
            <li>Click <strong>Save Changes</strong> when you're done. A backup is created automatically before saving.</li>
          </ol>
        </div>

        <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-5">
          <h3 className="font-semibold text-amber-800 dark:text-amber-300 mb-2">Important</h3>
          <p className="text-sm text-amber-700 dark:text-amber-400">
            Always close your world in Minecraft before opening it in Bedrock Toolchest, and always close
            it in Bedrock Toolchest before opening it in Minecraft. Having the world open in both at the
            same time can cause corruption or crashes. The program will warn you if it detects the world
            may be in use.
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">System requirements</h3>
          <ul className="space-y-1 text-sm">
            <li><strong>OS:</strong> Windows 10 or later (64-bit)</li>
            <li><strong>Disk:</strong> ~150 MB for the application</li>
            <li><strong>Other:</strong> No internet connection required</li>
          </ul>
        </div>
      </div>
    </section>
  );
}
